import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';

import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { TradeItem, TERMINAL_TRADE_STATUSES, type BoPostingStateDetail, type FrameworkAgreement, type RowSelectionMode, type TermType } from '@/types';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useUIStore, useTradesFilterStore, useTradesDataStore, useTradesColumnStore, useViewsStore, useToastStore } from '@/stores';
import { useTradesInfinite, useTradesSummary, useGiveNotice, useSetSettlementDate, useTerminateTrade, usePartialRecall, useSettleTrade, useSettleCash, useSettleSecurities, useRetryTrade, useHolidays, useCounterpartyTabs, useCounterpartyIdsByName, useUpdatePosition, useReRate, useTradeDetail } from '@/hooks';
import { useCounterpartyDetail } from '@/hooks/counterparty';
import { useHeldWhile } from '@/hooks/common';
import { SearchBar } from '@/components/ui/SearchBar';
import { createTradesColumns } from '../components';
import { TradesTable } from '../components/tables/TradesTable';
import { TradesModeTabs } from '../components/TradesModeTabs';
import { HistorySubTabs } from '../components/HistorySubTabs';
import { HistoryShell } from '../components/history/HistoryShell';
import { ScrollToTopButton } from '@/components/ui/ScrollToTopButton';
import { formatDate, todayLocalKey } from '@/utils/format';
import { calculateTradesColumnWidths } from '@/utils/trades/column-width';
import { buildSections } from '@/utils/trades/section-grouping';
import { singleAgreementType } from '@/utils/trades/agreement-sections';
import { SetCloseDateDialog } from '../components/SetCloseDateDialog';
import { TRADES_FILTERABLE_COLUMN_IDS, TRADES_COLUMN_ID_TO_FILTER_ID, getActiveTradesFilterColumnIds } from '@/utils/trades/filter-column-mapping';
import { PencilSquareIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ContextMenu } from '@/components/ui/ContextMenu';
import { HelpLink } from '@/features/help';
import { TourSentinel } from '@/features/onboarding';
import { useTradesContextMenus } from '../hooks/useTradesContextMenus';
import { AppliedFiltersStrip } from '@/features/inventory/components/AppliedFiltersStrip';
import { getAppliedTradesFilterChips } from '@/utils/trades/filter-chip-labels';
import type { TradesFilterOptionSets } from '@/utils/trades/filter-chip-labels';
import {
  TRADE_AGREEMENT_TYPE_OPTIONS,
  TRADE_STATUS_OPTIONS,
  TRADE_TERM_TYPE_OPTIONS,
  TRADE_TYPE_OPTIONS,
} from '@/features/trades/constants/filter-options';
import { computeVisualColIds } from '@/utils/cell-selection-utils';
import { ViewTabBar, MetricsBar } from '../components/views';
import { FloatingActionBar } from '../components';
import { TermFilterPill } from '../components/TermFilterPill';
import { fabClearanceFor } from '../components/fab-geometry';
import { BulkActionBar } from '../components/BulkActionBar';
import { BulkReRateModal } from '../components/bulk-rerate/BulkReRateModal';
import { ReRateModal } from '../components/trade-detail/modals/ReRateModal';
import { PartialRecallModal } from '../components/PartialRecallModal';
import { AmendTradeModalConnector } from '../components/AmendTradeModalConnector';
import { canPerform, collateralLegName, type ActionId } from '../hooks/trade-actions';
import { useRowSelection, applySelectionMode } from '../hooks/use-row-selection';
import { useAuth } from '@/hooks/auth/use-auth';
import { useKeyboardShortcuts } from '../hooks/use-keyboard-shortcuts';
import { useUrlViewSync } from '../hooks/use-url-view-sync';
import { useTradesViewBootstrap } from '../hooks/use-trades-view-bootstrap';
import { applyLocalTradeFilters } from '@/utils/trades/apply-trade-filters';
import { computeViewCountsFromSummary, isTradeInView } from '@/utils/trades/view-trade-counts';
import { buildTradesServerParams, buildTradesTabCountParams, buildTradesOptionsParams, buildTradesTermPillParams } from '@/utils/trades/map-server-params';
import { resolveTradesChrome } from '../trades-mode';
import { resolveBlotterLoadState } from '@/utils/trades/blotter-load-state';
import { collateralSearchForTrade } from '@/features/collateral/collateral-search';
import {
  boPostingCopy,
  boPostingHeadline,
  guardRowFor,
  resolveBoReason,
  retryBlockedReason,
  LEGACY_BO_ERROR_CAPTION,
} from '@/utils/trades/bo-posting-copy';

/**
 * LR-712: why BackOffice is in the way, in one line, inside a confirm dialog.
 *
 * A confirm dialog is not an inspector — the operator is deciding whether to go
 * ahead, and what decides it is WHICH posting is stuck and whether BackOffice may
 * already hold it. That comes from the posting's own state, so this needs no
 * detail fetch: `bo_posting` is on the blotter row.
 *
 * These two dialogs used to dump the raw `last_bo_error` blob into a monospace
 * box. Besides being unreadable at a decision point, it was the column that named
 * whichever posting failed LAST — so a Retry confirm could show "account not
 * found" for a trade whose actual blocker was an unconfirmed transfer, and Retry
 * is precisely the action that must not be taken on one of those.
 */
const BoReasonNote: React.FC<{ trade: TradeItem | null }> = ({ trade }) => {
  const { posting, legacy } = resolveBoReason(trade);
  if (!posting && !legacy) return null;
  return (
    <div className="mt-3 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-700">
      {posting ? (
        boPostingHeadline(posting)
      ) : (
        <>
          <span className="block break-all font-mono">{legacy}</span>
          <span className="mt-1 block italic text-gray-500">{LEGACY_BO_ERROR_CAPTION}</span>
        </>
      )}
    </div>
  );
};

export const TradesPage: React.FC = () => {
  const navigate = useNavigate();
  // LR-376: 'history' mode shows terminal positions only; 'trades' (default)
  // hides them. Stored in URL so refresh / bookmarks preserve the view.
  const { mode, htab } = useSearch({ from: '/trades' });
  const isHistoryMode = mode === 'history';
  // LR-390: History → Mark to Market subtab renders the MTM run log instead of the trades table.
  const showMtmHistory = isHistoryMode && htab === 'mtm';
  // LR-479: History → Cash Pool subtab renders the repo cash-pool posting log. Both it and the
  // MTM-run log are self-contained views (no trades table / filter sidebar).
  const showCashPoolHistory = isHistoryMode && htab === 'cashpool';
  const showHistoryLog = showMtmHistory || showCashPoolHistory;
  // LR-672: the counterparty tab is the section's scope axis, orthogonal to the
  // Live / History mode — History → Trades shows the same ViewTabBar (without
  // the MetricsBar); the MTM / Cash Pool logs keep their own rails.
  const { showViewTabBar, showMetricsBar } = resolveTradesChrome(mode, htab);
  // The counterparties that get a tab, by name, a page at a time: the first page
  // is the auto tabs, ViewTabBar pages the rest into its menu. The registry itself (tens of
  // thousands of rows, nearly all deactivated) is never loaded.
  const cpTabs = useCounterpartyTabs('trades');
  const cpListsLoaded = cpTabs.data !== undefined || cpTabs.isError;
  // LR-671: sort order lives in trades-data-store (persisted) so a saved view
  // can capture it and the dirty check can see it — was local useState.
  const sorting = useTradesDataStore((s) => s.tradesSorting);
  const setSorting = useTradesDataStore((s) => s.setTradesSorting);
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  // LR-600: the floating bar's measured height drives the table's scroll clearance —
  // it wraps to a second button row on narrow viewports, where a fixed clearance
  // left the last data row stranded behind the bar. Both bars write here; they are
  // mutually exclusive and each reports 0 on unmount, and React detaches the old
  // ref before attaching the new one, so the value always describes the live bar.
  const [actionBarHeight, setActionBarHeight] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Views system hooks
  const activeViewId = useViewsStore((s) => s.activeViewId);
  const views = useViewsStore((s) => s.views);
  const activeView = useMemo(() => views.find((v) => v.id === activeViewId), [views, activeViewId]);
  const syncCounterpartyTabs = useViewsStore((s) => s.syncCounterpartyTabs);
  const selection = useRowSelection(activeViewId);
  const addToast = useToastStore((s) => s.addToast);
  const showToast = useCallback((message: string) => addToast('error', message), [addToast]);
  useUrlViewSync(showToast, cpListsLoaded ? (cpTabs.firstPage ?? []) : undefined);
  // LR-671: `?view_id=` (saved view: columns / pins / filters / sorting) is the
  // source of truth, independent of the counterparty tab above.
  useTradesViewBootstrap();

  // Auto-generated per-CP views — sourced from the counterparties endpoint so
  // CPs with zero trades still get their tab. Runs whenever a list refreshes
  // (including after the TanStack Query 5-minute staleTime elapses or a new CP
  // is created elsewhere and the query is invalidated).
  useEffect(() => {
    if (!cpTabs.firstPage) return;
    syncCounterpartyTabs(
      cpTabs.firstPage
        .filter((cp) => Boolean(cp.name) && cp.name.trim() !== '')
        .map(({ id, name, isActive }) => ({ id, name, isActive })),
    );
  }, [cpTabs.firstPage, syncCounterpartyTabs]);
  const setShowFilterSidebar = useUIStore((state) => state.setShowFilterSidebar);
  const setFilterSidebarCollapsed = useUIStore((state) => state.setFilterSidebarCollapsed);
  const setFocusedFilterId = useUIStore((state) => state.setFocusedFilterId);
  const expandFilter = useUIStore((state) => state.expandFilter);
  const [appliedFiltersExpanded, _setTradesExpanded] = useState(() => localStorage.getItem('tradesFiltersExpanded') === 'true');
  const setAppliedFiltersExpanded = useCallback((v: boolean) => { _setTradesExpanded(v); localStorage.setItem('tradesFiltersExpanded', String(v)); }, []);
  const searchQuery = useTradesDataStore((state) => state.searchQuery);
  const setSearchQuery = useTradesDataStore((state) => state.setSearchQuery);
  const localTradesFilters = useTradesFilterStore((state) => state.localTradesFilters);
  const setLocalTradesFilters = useTradesFilterStore((state) => state.setLocalTradesFilters);
  const setFilteredTradesData = useTradesDataStore((state) => state.setFilteredTradesData);
  const setTradesListParams = useTradesDataStore((state) => state.setTradesListParams);
  const setTradesTotalCount = useTradesDataStore((state) => state.setTradesTotalCount);
  const setIsTradesDataLoading = useTradesDataStore((state) => state.setTradesDataLoading);
  const setTradesDataError = useTradesDataStore((state) => state.setTradesDataError);
  const setIsCreateTradeModalOpen = useTradesDataStore((state) => state.setIsCreateTradeModalOpen);

  // LR-257: gate trade-book mutations on the `create_trades` capability. The
  // backend enforces it too (403); this hides the controls from non-traders.
  const { hasPermission } = useAuth();
  const canTrade = hasPermission('create_trades');

  const { mutate: giveNotice, isPending: isGivingNotice, error: noticeError, reset: resetNotice } = useGiveNotice();
  const { mutate: setSettlementDate, isPending: isSettingSettlementDate, error: settlementDateError, reset: resetSettlementDate } = useSetSettlementDate();
  const { mutate: terminateTrade, isPending: isTerminating, error: terminateError, reset: resetTerminate } = useTerminateTrade();
  // LR-388: whole handle (mutate/isPending/error/reset) is passed to PartialRecallModal.
  const partialRecall = usePartialRecall();
  // LR-484: page-owned amend mutation handle, forwarded to the amend modal (mirrors
  // partialRecall above). It invalidates tradesKeys.all on success → the list refetches.
  const updatePosition = useUpdatePosition();
  const { mutate: settleTrade, isPending: isSettling, error: settleError, reset: resetSettle } = useSettleTrade();
  const { mutate: settleCash, isPending: isSettlingCash, error: settleCashError, reset: resetSettleCash } = useSettleCash();
  const { mutate: settleSecurities, isPending: isSettlingSecurities, error: settleSecuritiesError, reset: resetSettleSecurities } = useSettleSecurities();
  const { mutate: retryTrade, isPending: isRetrying, error: retryError, reset: resetRetry } = useRetryTrade();

  const [noticeTrade, setNoticeTrade] = useState<TradeItem | null>(null);
  const [noticeDate, setNoticeDate] = useState<string>(todayLocalKey());

  const [settlementDateTrade, setSettlementDateTrade] = useState<TradeItem | null>(null);
  const [actualSettlementDate, setActualSettlementDate] = useState<string>(todayLocalKey());
  const [settlementReason, setSettlementReason] = useState<string>('');

  const [terminateTradeItem, setTerminateTradeItem] = useState<TradeItem | null>(null);
  const [terminateSettlementDate, setTerminateSettlementDate] = useState<string>(todayLocalKey());
  const [terminateReason, setTerminateReason] = useState<string>('');

  // LR-408: rejected → cancelled uses the same terminate endpoint but a
  // simpler modal (no settlement date — position never opened, nothing to
  // schedule).
  const [cancelRejectedItem, setCancelRejectedItem] = useState<TradeItem | null>(null);
  const [cancelRejectedReason, setCancelRejectedReason] = useState<string>('');

  // LR-408: BO error details modal — opens on click of the Rejected badge
  // (or the retry counter chip for pending positions with prior failures).
  const [boErrorTrade, setBoErrorTrade] = useState<TradeItem | null>(null);
  const [boErrorCopied, setBoErrorCopied] = useState(false);
  // LR-712: the inspector reads BackOffice's own response off the posting record,
  // which lives on the DETAIL payload only — LR-711 kept `response_body` off the
  // list shape deliberately, since it would cost a body per row to serve a text
  // nobody reads until they click. Fetched on open, the same lazy pattern the
  // re-rate modal uses below.
  const { data: boErrorDetail, isLoading: boErrorLoading } = useTradeDetail(
    boErrorTrade?.id ?? 0, boErrorTrade !== null,
  );
  // The posting record is authoritative when it exists. The legacy column is read
  // ONLY in its absence — a trade rejected before LR-709 shipped the attempt log,
  // whose backfill had no source for `rejected` — so the string can never be shown
  // contradicting a posting state, which was the TRD-111 defect.
  // While the detail is in flight the ROW answers: it carries `bo_posting` too,
  // just without the response body. So the state is on screen from the first
  // frame and only BackOffice's verbatim text arrives late — rather than the
  // modal opening blank on the trades that matter most.
  // Typed as the detail shape on purpose: the row's `BoPostingState` is a subset
  // of it (the extra keys are all optional), so an early frame renders with
  // `response_body` simply absent instead of forcing the modal to wait.
  const { posting: boErrorPosting, legacy: boErrorLegacy } =
    resolveBoReason<BoPostingStateDetail>(boErrorDetail ?? boErrorTrade);
  const boErrorBody = boErrorPosting?.response_body || boErrorLegacy;

  // LR-408: retry confirm modal — confirms operator has fixed underlying
  // BO data before resetting rejected → pending.
  const [retryTradeItem, setRetryTradeItem] = useState<TradeItem | null>(null);
  // LR-714 round 2: the book as it is NOW, for a guard that runs long after the
  // dialog opened. `retryTradeItem` is a snapshot, and the book refreshes itself
  // on window focus and every five minutes — see `confirmRetry`. Same technique
  // as `sortedDataRef` below, declared up here because the callback is.
  const currentTradesRef = useRef<TradeItem[]>([]);

  // Bulk-fetch holidays once (cached for a day). The modal filters locally
  // by the chosen date — no per-keystroke API hits.
  const { data: holidays } = useHolidays(!!settlementDateTrade);

  const [settleTradeItem, setSettleTradeItem] = useState<TradeItem | null>(null);
  const [settleCashItem, setSettleCashItem] = useState<TradeItem | null>(null);
  const [settleSecuritiesItem, setSettleSecuritiesItem] = useState<TradeItem | null>(null);
  // LR-388: the position a partial recall/return is being booked against (null = closed).
  const [partialRecallTrade, setPartialRecallTrade] = useState<TradeItem | null>(null);
  // LR-484: the pending trade whose numbers are being amended (resolved to full
  // PositionDetail by AmendTradeModalConnector before the modal mounts).
  const [amendTrade, setAmendTrade] = useState<TradeItem | null>(null);

  const handleGiveNotice = useCallback((trade: TradeItem) => {
    resetNotice();
    setNoticeTrade(trade);
    // LR-696: local-zone day, like handleSetSettlementDate below and the card's
    // NoticeModal (LR-208 r5 M1). A UTC slice hands a viewer west of UTC
    // "tomorrow" in the evening and one east of UTC "yesterday" before 03:00.
    //
    // This is the dialog where the wrong default costs most, and it is the one the
    // backend does not catch (round 2): `Position.give_notice` never validates the
    // date against today — it writes `notice_served_date` verbatim and sets
    // `maturity_date = notice_date + notice_period_days`. So a yesterday default is
    // accepted in silence, and the notice is on record as served a day early with
    // the term a day shorter. That is a contractual fact, not a UI annoyance.
    setNoticeDate(todayLocalKey());
  }, [resetNotice]);

  const handleSetSettlementDate = useCallback((trade: TradeItem) => {
    resetSettlementDate();
    setSettlementDateTrade(trade);
    // LR-411: default to today. `trade.settlement_date` is the OPEN
    // settlement date — irrelevant here and almost always in the past,
    // which then trips the model-side past-date guard. Today is the
    // sensible default for a close-side date pick. Local-zone key, matching
    // the floor SetCloseDateDialog computes — a UTC-sliced default can open
    // the picker below its own min.
    setActualSettlementDate(todayLocalKey());
    setSettlementReason('');
  }, [resetSettlementDate]);

  const handleTerminate = useCallback((trade: TradeItem) => {
    resetTerminate();
    // LR-408: rejected positions go through a date-less cancel modal —
    // they never opened in BO, so a "Termination Date" is meaningless.
    if (trade.status === 'rejected') {
      setCancelRejectedItem(trade);
      setCancelRejectedReason('');
      return;
    }
    setTerminateTradeItem(trade);
    // LR-696: local-zone day (see handleGiveNotice). With a UTC slice a trader
    // west of UTC could terminate with tomorrow's date without noticing — the
    // backend takes a future settlement date — while one east of UTC gets
    // yesterday. Round 4 closed that second half at the source: both book fields
    // now carry `min={todayLocalKey()}`, as the card's TerminateModal always has
    // — so the trip to a 400 is unreachable rather than merely survivable.
    setTerminateSettlementDate(todayLocalKey());
    setTerminateReason('');
  }, [resetTerminate]);

  const confirmCancelRejected = useCallback(() => {
    // Re-check validity at submit time: a background refetch (or another
    // operator) may have moved this position off `rejected` while the modal
    // was open. terminate on a now-pending position would silently stamp a
    // bogus close-settlement date (see trade-actions.ts) — guard like every
    // other lifecycle confirm handler.
    if (cancelRejectedItem && canPerform(cancelRejectedItem, 'terminate')) {
      terminateTrade(
        { id: cancelRejectedItem.id, reason: cancelRejectedReason || 'Cancelled after BO rejection.' },
        { onSuccess: () => setCancelRejectedItem(null) },
      );
    }
  }, [cancelRejectedItem, cancelRejectedReason, terminateTrade]);

  const handleMarkToMarket = useCallback((trade: TradeItem) => {
    // The trade's own book decides the workspace (LR-587).
    navigate({ to: '/collateral', search: collateralSearchForTrade(trade, trade.counterpartyId) });
  }, [navigate]);

  const handleSettle = useCallback((trade: TradeItem) => {
    resetSettle();
    setSettleTradeItem(trade);
  }, [resetSettle]);

  const handleSettleCash = useCallback((trade: TradeItem) => {
    resetSettleCash();
    setSettleCashItem(trade);
  }, [resetSettleCash]);

  const handleSettleSecurities = useCallback((trade: TradeItem) => {
    resetSettleSecurities();
    setSettleSecuritiesItem(trade);
  }, [resetSettleSecurities]);

  // Open the single-trade page for the selected position (row action panel).
  // Plain SPA navigation — no `reloadDocument`, so AuthGuard stays mounted and the
  // user doesn't see the "Checking authentication…" flash on every open. This used
  // to silently revert to /trades (hence the old reloadDocument workaround): the
  // view→URL sync (use-url-view-sync) re-fired during the pending transition and
  // navigated back. That sync is now idempotent, so the transition sticks (LR-208).
  const handleOpen = useCallback((trade: TradeItem) => {
    // LR-478 review r5: carry the SAME origin the trade-ID cell Link stamps
    // (createTradesColumns origin), so opening via the FAB — the primary open
    // affordance — yields the same breadcrumb return target. Without this, opening
    // a History-mode row from the FAB returned the operator to the live blotter.
    navigate({
      to: '/trades/$tradeId',
      params: { tradeId: String(trade.id) },
      search: { from: isHistoryMode ? 'history' : 'trades', cp: undefined },
    });
  }, [navigate, isHistoryMode]);

  // LR-408: Retry rejected position. Confirm via modal so operator
  // acknowledges they fixed the underlying BO data before re-queueing.
  const handleRetry = useCallback((trade: TradeItem) => {
    resetRetry();
    setRetryTradeItem(trade);
  }, [resetRetry]);

  const confirmRetry = useCallback(() => {
    if (!retryTradeItem) return;
    // The row as it is NOW, not as the dialog captured it. `retryTradeItem` is a
    // snapshot taken on click, while the book refreshes itself on window focus and
    // every five minutes (`use-trades-infinite`) — and the dialog's own copy asks
    // the operator to go and check BackOffice, i.e. leave the window and come
    // back, which is exactly the refresh. Re-reading by id, the same way
    // `selectedTrade` does, is what makes this guard's promise true instead of
    // decorative (LR-714 round 2); on an unchanged row it is the same object and
    // the same answers.
    //
    // BOTH halves of the gate judge it (round 3): the status check below said
    // "re-check validity at submit time" while reading the snapshot, so a trade
    // that left `rejected` while the dialog was open still reached the mutation
    // and came back 400. Two gates on one action reading two different objects is
    // the drift this whole pair of rounds is about.
    const current = guardRowFor(retryTradeItem, currentTradesRef.current);
    // Same rationale as confirmCancelRejected: only a still-`rejected` position
    // may be re-queued.
    if (!canPerform(current, 'retry')) return;
    // LR-714, second half of the same gate (PITFALLS #7 — guard the full action
    // path, not just the button). The bar disables Retry while BackOffice may
    // already hold the posting; this catches the paths that reach the mutation
    // with the button never re-consulted — a stale render, a keyboard race, or a
    // dialog left open while the row's posting changed underneath it.
    //
    // Same predicate the bar reads, so the two cannot drift; the toast repeats
    // the tooltip rather than failing silently.
    const blockedReason = retryBlockedReason(current.bo_posting);
    if (blockedReason) {
      addToast('error', blockedReason);
      setRetryTradeItem(null);
      return;
    }
    retryTrade(
      { id: retryTradeItem.id },
      { onSuccess: () => setRetryTradeItem(null) },
    );
  }, [retryTradeItem, retryTrade, addToast]);

  // LR-408: open the BO-error detail modal (panel action for rejected rows).
  const handleShowBoError = useCallback((trade: TradeItem) => {
    setBoErrorTrade(trade);
    setBoErrorCopied(false);
  }, []);

  const copyBoError = useCallback(async () => {
    if (!boErrorBody) return;
    try {
      await navigator.clipboard.writeText(boErrorBody);
      setBoErrorCopied(true);
      setTimeout(() => setBoErrorCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (HTTP context, perms) — best-effort.
    }
  }, [boErrorBody]);

  // Submit-path guard (ACTN-02 / PITFALLS #7): every confirm handler re-checks
  // the shared `canPerform` predicate against the freshly-loaded row before
  // mutating — the SAME predicate the row buttons render-gate through — so the
  // two action surfaces (detail bar + Trades page) cannot drift and a
  // stale/keyboard/race submit no-ops.
  //
  // Dispatch a panel lifecycle action to its existing confirm-dialog handler.
  // (LR-408 'retry' routes here too; 'terminate' on a rejected row opens the
  // date-less cancel modal via handleTerminate.)
  const handlePartialRecall = useCallback((trade: TradeItem) => {
    setPartialRecallTrade(trade);
  }, []);

  const handleAmend = useCallback((trade: TradeItem) => {
    setAmendTrade(trade);
  }, []);

  // LR-539: single re-rate from the blotter FAB. The blotter row is a TradeItem,
  // but ReRateModal (and its daily-P&L preview) needs the fuller PositionDetail
  // (margin_bps / day_count_convention / available_quantity), so fetch the detail
  // on demand and open the same modal the detail page uses once it has loaded.
  const [reRateTradeId, setReRateTradeId] = useState<number | null>(null);
  const reRate = useReRate();
  const { data: reRateDetail } = useTradeDetail(reRateTradeId ?? 0, reRateTradeId !== null);
  const handleReRate = useCallback((trade: TradeItem) => setReRateTradeId(trade.id), []);

  const handlePanelAction = useCallback((actionId: ActionId, trade: TradeItem) => {
    switch (actionId) {
      case 'give_notice': handleGiveNotice(trade); break;
      case 'set_settlement_date': handleSetSettlementDate(trade); break;
      case 'settle': handleSettle(trade); break;
      case 'settle_cash': handleSettleCash(trade); break;
      case 'settle_securities': handleSettleSecurities(trade); break;
      case 'retry': handleRetry(trade); break;
      case 'partial_recall': handlePartialRecall(trade); break;
      case 'amend': handleAmend(trade); break;
      case 're_rate': handleReRate(trade); break;
      case 'terminate': handleTerminate(trade); break;
    }
  }, [handleGiveNotice, handleSetSettlementDate, handleSettle, handleSettleCash, handleSettleSecurities, handleRetry, handlePartialRecall, handleAmend, handleReRate, handleTerminate]);

  const confirmSettleCash = useCallback(() => {
    if (settleCashItem && canPerform(settleCashItem, 'settle_cash')) {
      settleCash(
        { id: settleCashItem.id },
        { onSuccess: () => setSettleCashItem(null) },
      );
    }
  }, [settleCashItem, settleCash]);

  const confirmSettleSecurities = useCallback(() => {
    if (settleSecuritiesItem && canPerform(settleSecuritiesItem, 'settle_securities')) {
      settleSecurities(
        { id: settleSecuritiesItem.id },
        { onSuccess: () => setSettleSecuritiesItem(null) },
      );
    }
  }, [settleSecuritiesItem, settleSecurities]);

  const confirmGiveNotice = useCallback(() => {
    if (noticeTrade && canPerform(noticeTrade, 'give_notice')) {
      giveNotice(
        { id: noticeTrade.id, noticeDate },
        {
          onSuccess: () => {
            setNoticeTrade(null);
          },
        }
      );
    }
  }, [noticeTrade, giveNotice, noticeDate]);

  const confirmSetSettlementDate = useCallback(() => {
    // A native date input can be cleared to ''; the button is disabled for that,
    // but a keyboard/race submit must not fire the guaranteed 400 either.
    if (!actualSettlementDate) return;
    if (settlementDateTrade && canPerform(settlementDateTrade, 'set_settlement_date')) {
      setSettlementDate(
        { id: settlementDateTrade.id, actualSettlementDate, reason: settlementReason },
        {
          onSuccess: () => {
            setSettlementDateTrade(null);
          },
        }
      );
    }
  }, [settlementDateTrade, setSettlementDate, actualSettlementDate, settlementReason]);

  const confirmTerminate = useCallback(() => {
    if (terminateTradeItem && canPerform(terminateTradeItem, 'terminate')) {
      const id = terminateTradeItem.id;
      terminateTrade(
        { id, actualSettlementDate: terminateSettlementDate, reason: terminateReason },
        {
          onSuccess: (updated) => {
            setTerminateTradeItem(null);
            // LR-695: a future-dated Terminate leaves the row Active until the
            // end date it fixed. The blotter has no success toast otherwise,
            // but here the row visibly does not change, so say what happened
            // or the trader terminates it again.
            if (updated?.status === 'active') {
              addToast(
                'success',
                `Termination scheduled: #${id} ends on ${formatDate(terminateSettlementDate)} and stays active until then`,
              );
            }
          },
        }
      );
    }
  }, [terminateTradeItem, terminateTrade, terminateSettlementDate, terminateReason, addToast]);

  const confirmSettle = useCallback(() => {
    if (settleTradeItem && canPerform(settleTradeItem, 'settle')) {
      settleTrade(
        { id: settleTradeItem.id },
        {
          onSuccess: () => {
            setSettleTradeItem(null);
          },
        }
      );
    }
  }, [settleTradeItem, settleTrade]);

  // name -> FK id so the CP scope is sent comma-safe (see counterparty_ids). A tab knows its
  // own counterparty's id, so its book never waits for a list; the sidebar filter and saved
  // views keep names, and a name no list holds is looked up on its own.
  const knownCpNameToId = useMemo(() => {
    const map: Record<string, number> = {};
    for (const cp of cpTabs.rows) map[cp.name] = cp.id;
    for (const v of views) {
      if (v.counterpartyId !== undefined && v.counterparties.length === 1) {
        map[v.counterparties[0]] = v.counterpartyId;
      }
    }
    return map;
  }, [cpTabs.rows, views]);
  const scopeCpNames = useMemo(
    () => [...new Set([...(activeView?.counterparties ?? []), ...localTradesFilters.counterparties])],
    [activeView, localTradesFilters.counterparties],
  );
  const unresolvedCpNames = useMemo(
    () => scopeCpNames.filter((name) => knownCpNameToId[name] === undefined),
    [scopeCpNames, knownCpNameToId],
  );
  // Only once the lists are in: a name they hold needs no request of its own.
  const cpLookup = useCounterpartyIdsByName(unresolvedCpNames, { enabled: cpListsLoaded });
  const cpNameToId = useMemo(
    () => ({ ...knownCpNameToId, ...cpLookup.nameToId }),
    [knownCpNameToId, cpLookup.nameToId],
  );

  // Server-driven windowed blotter: server does filters/search/sort; a thin client
  // filter pass (filteredTradesData) handles what the server can't (daily earnings).
  const serverParams = useMemo(
    () => buildTradesServerParams({
      localFilters: localTradesFilters,
      searchQuery,
      sorting,
      viewCounterparties: activeView?.counterparties ?? [],
      cpNameToId,
      mode,
    }),
    [localTradesFilters, searchQuery, sorting, activeView, cpNameToId, mode],
  );
  // By value: a re-render that rebuilds equal params must not count as a new selection.
  // The Daily Earnings bounds are client-only, not in serverParams, but they change the
  // rows too: they also refill the loader's auto-fill budget and open at the first row.
  const serverParamsKey = useMemo(
    () => JSON.stringify([serverParams, localTradesFilters.dailyEarningsMin, localTradesFilters.dailyEarningsMax]),
    [serverParams, localTradesFilters.dailyEarningsMin, localTradesFilters.dailyEarningsMax],
  );
  // A per-CP view (or a sidebar CP filter) needs its names resolved to FK ids first. Until
  // they are, buildTradesServerParams would send counterparty_ids=-1 (match-nothing) and
  // flash an empty blotter on F5 before the ids arrive and refetch. Gate the fetch on the
  // resolution instead of firing a known-empty query. A name the registry does
  // not know resolves to "match nothing", as it should.
  const needsCpRegistry = scopeCpNames.length > 0;
  const cpRegistryReady =
    unresolvedCpNames.length === 0 ||
    (cpListsLoaded && !cpLookup.isPending && cpLookup.error === null);
  // The tab list failing blocks nothing: the names it would have held are looked up by name.
  const cpRegistryError = cpLookup.error;
  const {
    trades: allTradesData,
    totalCount,
    isLoading,
    isPlaceholderData,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTradesInfinite(serverParams, !showHistoryLog && (!needsCpRegistry || cpRegistryReady));
  // A disabled TanStack query reports isLoading=false, and v5 flips a query to `error`
  // on a failed refetch even while cached data persists — so the blotter's load/error
  // state is derived (not read straight off the queries): registry-pending → skeleton;
  // registry-down → error; registry-stale or list-refetch-failed-over-cache → keep
  // serving the book. See resolveBlotterLoadState (kvi-r2 #4 / r3 #1 / r4 #1).
  const {
    isLoading: isBlotterLoading,
    isError: isBlotterError,
    error: blotterError,
  } = resolveBlotterLoadState({
    tradesLoading: isLoading,
    tradesError: error,
    hasTradesData: totalCount !== undefined,
    needsCpRegistry,
    cpRegistryReady,
    cpRegistryError,
  });
  // The three sidebar/pill summaries also resolve counterparty_ids; gate them on the same
  // registry-readiness so they don't fire a known-empty counterparty_ids=-1 request during
  // the registry-load window (kvi-r3 #3).
  const cpScopeReady = !needsCpRegistry || cpRegistryReady;

  // Tab badges come from the server aggregate (not the loaded window, which only
  // holds the active view's page). Mode-scoped only — independent of sidebar/search
  // (LR-104 review L-2).
  const { data: tabCountSummary, isError: tabCountsFailed } = useTradesSummary(
    'counts',
    buildTradesTabCountParams(mode),
    !showHistoryLog,
  );

  // Whole-book option pools for the sidebar dropdowns (stable — no sidebar filters/
  // search), published to the store for FilterSidebar instead of the loaded window.
  const optionsParams = useMemo(
    () => buildTradesOptionsParams(mode, activeView?.counterparties ?? [], cpNameToId),
    [mode, activeView, cpNameToId],
  );
  const { data: optionsSummary } = useTradesSummary(
    'options',
    optionsParams,
    !showHistoryLog && cpScopeReady,
  );

  // LR-700: Term-pill counts for HISTORY only — in live mode MetricsBar fetches
  // (and renders) the pill itself, so this query stays disabled there rather than
  // duplicating that request. Scoped like the pill: everything except its own
  // `term_types` predicate, `mode` included so History counts terminal statuses.
  const termPillParams = useMemo(
    () =>
      buildTradesTermPillParams({
        localFilters: localTradesFilters,
        searchQuery,
        viewCounterparties: activeView?.counterparties ?? [],
        cpNameToId,
        mode,
      }),
    [localTradesFilters, searchQuery, activeView, cpNameToId, mode],
  );
  const { data: termPillSummary } = useTradesSummary(
    'term_type_totals',
    termPillParams,
    isHistoryMode && !showHistoryLog && cpScopeReady,
  );
  const handleTermTypesChange = useCallback(
    (termTypes: string[]) =>
      setLocalTradesFilters({ ...localTradesFilters, termTypes: termTypes as TermType[] }),
    [localTradesFilters, setLocalTradesFilters],
  );

  // LR-469 footer: whole-book, OPEN-book per-column subtotals, scoped EXACTLY like the
  // list (view∩sidebar CP + sidebar filters + trade_types + search) so the footer
  // totals match the visible rows — minus `ordering` (irrelevant to an aggregate;
  // dropping it avoids a needless refetch on sort). The metrics-bar summary drops
  // trade_types (its L/B & Repo buttons ARE that filter), so the footer can't reuse it.
  const footerMetricsParams = useMemo(() => {
    const { ordering: _ordering, ...rest } = serverParams;
    return rest;
  }, [serverParams]);
  // Live blotter only — History is a plain terminal log (its rows are never open-book,
  // and the MetricsBar is hidden there too), so it carries no totals footer.
  const { data: footerSummary } = useTradesSummary(
    'column_totals',
    footerMetricsParams,
    !isHistoryMode && cpScopeReady,
  );

  const setFilterOptionPools = useTradesDataStore((s) => s.setFilterOptionPools);
  useEffect(() => {
    if (optionsSummary) {
      setFilterOptionPools({ symbols: optionsSummary.symbols, currencies: optionsSummary.currencies });
    }
  }, [optionsSummary, setFilterOptionPools]);

  // Columns are pure data + informational badges; lifecycle actions live in the
  // row action panel (FloatingActionBar), so no handlers are wired into cells.
  // LR-478: stamp the row's origin onto the trade-ID Link so the single-trade
  // breadcrumb returns to the surface the row was opened from — History rows
  // carry from='history', live blotter rows from='trades'. (MTM rows get
  // from='mtm' + cp via createMtmColumns on the MTM page.)
  const allColumns = useMemo(
    () => createTradesColumns({ origin: { from: isHistoryMode ? 'history' : 'trades' } }),
    [isHistoryMode],
  );

  const columnConfig = useTradesColumnStore((state) => state.columns);
  const pinnedLeft = useTradesColumnStore((state) => state.pinnedLeft);
  const pinnedRight = useTradesColumnStore((state) => state.pinnedRight);
  const pinColumn = useTradesColumnStore((state) => state.pinColumn);
  const getPinState = useTradesColumnStore((state) => state.getPinState);
  const toggleColumnVisibility = useTradesColumnStore((state) => state.toggleColumnVisibility);
  const isColumnsTabActive = useUIStore((state) => state.columnsTabActive);

  const columns = useMemo(() => {
    const columnMap = new Map(allColumns.map((col) => [(col as { accessorKey?: string }).accessorKey || col.id, col]));

    // Get columns that are in our config
    const configuredColumns = columnConfig
      .filter((config) => config.visible)
      .map((config) => columnMap.get(config.id))
      .filter((col): col is NonNullable<typeof col> => col !== undefined);

    // Find any columns in allColumns that are NOT in columnConfig (new columns)
    const configIds = new Set(columnConfig.map((c) => c.id));
    const newColumns = allColumns.filter((col) => {
      const id = (col as { accessorKey?: string }).accessorKey || col.id || '';
      return !configIds.has(id);
    });

    return [...configuredColumns, ...newColumns];
  }, [columnConfig, allColumns]);

  const columnWidths = useMemo(() => {
    return calculateTradesColumnWidths(columns, allTradesData ?? []);
  }, [columns, allTradesData]);

  const activeFilterColumnIds = useMemo(
    () => getActiveTradesFilterColumnIds(localTradesFilters),
    [localTradesFilters]
  );

  // Locate column (from filter sidebar): scroll to header and highlight
  const locateColumnRequest = useUIStore((state) => state.locateColumnRequest);
  const setLocateColumnRequest = useUIStore((state) => state.setLocateColumnRequest);
  const locateHighlightedColumnIds = useUIStore((state) => state.locateHighlightedColumnIds);
  const setLocateHighlightedColumnIds = useUIStore((state) => state.setLocateHighlightedColumnIds);
  const highlightedColumnIds = locateHighlightedColumnIds ?? [];

  const handleFilterColumnClick = useCallback(
    (columnId: string) => {
      setFilterSidebarCollapsed(false);
      const filterId = TRADES_COLUMN_ID_TO_FILTER_ID[columnId] ?? columnId;
      setFocusedFilterId(filterId);
      expandFilter(filterId);
      setLocateHighlightedColumnIds(null);
    },
    [setFilterSidebarCollapsed, setFocusedFilterId, expandFilter, setLocateHighlightedColumnIds]
  );

  useEffect(() => {
    if (!locateColumnRequest) return;
    const { columnId } = locateColumnRequest;
    setLocateHighlightedColumnIds([columnId]);
    const el = document.querySelector(`[data-column-id="${columnId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    const t = setTimeout(() => {
      setLocateHighlightedColumnIds(null);
      setLocateColumnRequest(null);
    }, 2000);
    return () => clearTimeout(t);
  }, [locateColumnRequest, setLocateColumnRequest, setLocateHighlightedColumnIds]);

  const clearLocalTradesFilters = useTradesFilterStore((state) => state.clearLocalTradesFilters);
  const resetLocalFilter = useTradesFilterStore((state) => state.resetLocalFilter);
  const tradesOptionSets = useMemo<TradesFilterOptionSets>(
    () => ({
      statuses: TRADE_STATUS_OPTIONS,
      // No `counterparties` pool: the sidebar searches the registry on the server,
      // so there is no full list to collapse a chip to "all" against.
      tradeTypes: TRADE_TYPE_OPTIONS,
      agreementTypes: TRADE_AGREEMENT_TYPE_OPTIONS,
      termTypes: TRADE_TERM_TYPE_OPTIONS,
      // LR-474 r2 #5: no `currencies` pool — the sidebar derives its options
      // LIVE from the book, so collapsing the chip to "all" against the static
      // mock pool could claim "all" while live-only currencies are filtered
      // out. The chip lists the raw selection instead.
    }),
    []
  );
  const appliedFilterChips = useMemo(
    () => getAppliedTradesFilterChips(localTradesFilters, tradesOptionSets),
    [localTradesFilters, tradesOptionSets]
  );
  // The settlement-ccy filter is surfaced by the summary-bar pill (which now carries
  // its own blue active-outline), NOT by the applied-filters strip — so selecting a
  // currency no longer pops the strip open. Exclude it from the strip's chips + count;
  // the full list (incl. ccy) still drives the table's empty-state copy below.
  const stripChips = useMemo(
    () => appliedFilterChips.filter((c) => c.filterId !== 'settlementCurrencies'),
    [appliedFilterChips]
  );
  const handleAppliedChipClick = useCallback(
    (filterId: string) => {
      // LR-474 r2 #3: the settlement-ccy filter has no sidebar section — its
      // home is the summary-bar pill — so opening the sidebar would dead-end
      // (the chip's X still removes the filter).
      if (filterId === 'settlementCurrencies') return;
      setFilterSidebarCollapsed(false);
      setFocusedFilterId(filterId);
      expandFilter(filterId);
    },
    [setFilterSidebarCollapsed, setFocusedFilterId, expandFilter]
  );
  const handleAppliedChipRemove = useCallback(
    (filterId: string) => {
      resetLocalFilter(filterId);
    },
    [resetLocalFilter]
  );

  const sortedDataRef = useRef<TradeItem[]>([]);

  // Column id/index helpers (pinning + header context menu)
  const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
  const pinnedRightSet = useMemo(() => new Set(pinnedRight), [pinnedRight]);
  const tradesColIds = useMemo(
    () => computeVisualColIds(columns, pinnedLeftSet, pinnedRightSet),
    [columns, pinnedLeftSet, pinnedRightSet],
  );

  const {
    headerCtxMenu,
    headerCtxMenuItems,
    handleColumnContextMenu,
    closeHeaderCtxMenu,
  } = useTradesContextMenus({
    columnConfig,
    columns,
    sorting,
    setSorting,
    getPinState,
    pinColumn,
    toggleColumnVisibility,
    handleFilterColumnClick,
    localTradesFilters,
    setLocalTradesFilters,
    sortedDataRef,
    colIds: tradesColIds,
  });

  // LR-474 follow-up: a single-counterparty view only needs the metric section that
  // matches that CP's framework agreement (GMSLA → Stock L/B, GMRA → Repo). Resolve
  // it from the loaded tab list, else by the tab's id; null for All-Trades / multi-CP
  // views, a counterparty holding both agreements, and unknown CPs ⇒ MetricsBar shows
  // both sections.
  const activeTabCpId = activeView?.counterparties.length === 1 ? activeView.counterpartyId : undefined;
  const activeTabRow = cpTabs.rows.find((c) => c.id === activeTabCpId);
  const { data: activeTabDetail } = useCounterpartyDetail(
    activeTabCpId !== undefined && !activeTabRow && cpListsLoaded ? activeTabCpId : 0,
  );
  const activeTabCp =
    activeTabRow ?? (activeTabDetail?.id === activeTabCpId ? activeTabDetail : undefined);
  const activeViewAgreementType = useMemo<FrameworkAgreement | null>(
    () => (activeTabCpId === undefined ? null : singleAgreementType(activeTabCp?.agreements)),
    [activeTabCpId, activeTabCp],
  );
  // The open tab's name and "(inactive)" follow the registry: the sync refreshes only the first
  // page's tabs, and MetricsBar reads a tab by its name.
  const openCounterpartyTab = useViewsStore((s) => s.openCounterpartyTab);
  useEffect(() => {
    if (!activeTabCp || !activeView) return;
    if (activeTabCp.name !== activeView.name || activeTabCp.isActive === Boolean(activeView.isInactive)) {
      openCounterpartyTab({ id: activeTabCp.id, name: activeTabCp.name, isActive: activeTabCp.isActive });
    }
  }, [activeTabCp, activeView, openCounterpartyTab]);

  // Empty-state copy for the trades table. A counterparty tab with no trades is
  // a normal state (auto-tabs are created for every active CP, even zero-trade
  // ones), so it gets a tailored message rather than the filter-oriented default.
  const tableEmptyState = useMemo(() => {
    const hasFilters = searchQuery.trim() !== '' || appliedFilterChips.length > 0;
    if (hasFilters) {
      return {
        title: 'No matching trades',
        description: 'Try adjusting your filters or search query.',
      };
    }
    if (activeView && activeView.counterparties.length > 0) {
      return {
        title: `No trades for ${activeView.name}`,
        description: 'This counterparty has no trades yet.',
      };
    }
    return {
      title: 'No trades found',
      description: 'Trades will appear here once they are booked.',
    };
  }, [searchQuery, appliedFilterChips.length, activeView]);

  // Trade counts per view. Scoped to the current mode (history shows terminal
  // positions, default hides them) so the badge reflects what the tab will show,
  // but independent of sidebar/search filters (LR-104 review L-2).
  // Aggregation runs in a single pass via `computeViewTradeCounts` —
  // O(trades + Σ counterparties) instead of O(views × trades) (kvi-r1 #5).
  const viewTradeCounts = useMemo(
    () => computeViewCountsFromSummary(tabCountSummary, views),
    [tabCountSummary, views],
  );

  // View+mode-scoped data (no sidebar filters). Used as the stable basis for
  // category counters in MetricsBar so they don't shift when a tradeTypes
  // filter is active. LR-672: the tab scope applies in History too (the server
  // list is already scoped by counterparty_ids in both modes — this pass just
  // keeps the published set honest to the active tab).
  const viewScopedTradesData = useMemo(() => {
    let result = allTradesData || [];
    if (activeView && activeView.counterparties.length > 0) {
      result = result.filter((item) => isTradeInView(item, activeView));
    }
    if (isHistoryMode) {
      result = result.filter((item) => (TERMINAL_TRADE_STATUSES as readonly string[]).includes(item.status));
    } else {
      result = result.filter((item) => !(TERMINAL_TRADE_STATUSES as readonly string[]).includes(item.status));
    }
    return result;
  }, [allTradesData, activeView, isHistoryMode]);

  const setViewScopedTradesData = useTradesDataStore((state) => state.setViewScopedTradesData);

  // Sidebar filters on top of the view scope (shared with the MTM tab).
  // No client search or sort: both are server-side (serverParams), and a windowed
  // blotter re-sorting its loaded pages would float rows from later pages to the top.
  const scopedFilteredTradesData = useMemo(
    () => applyLocalTradeFilters(viewScopedTradesData, localTradesFilters),
    [viewScopedTradesData, localTradesFilters],
  );
  // While a new selection loads, the rows on screen are the previous one's (placeholder
  // data). Re-scoping them to a new tab or sidebar filter would show a stray subset or a
  // false "No trades" until the answer lands, so the table keeps what it showed.
  const filteredTradesData = useHeldWhile(scopedFilteredTradesData, isPlaceholderData);
  // The footer totals and the search highlight are held with those rows: the new
  // selection's totals can land first, and the new query would highlight old rows.
  const heldFooterColumnTotals = useHeldWhile(footerSummary?.column_totals, isPlaceholderData);
  const heldSearchQuery = useHeldWhile(searchQuery, isPlaceholderData);

  // LR-686: one headerless section in BOTH modes — the book is a flat list (no
  // REQUIRE-ATTENTION / open-book split, see buildSections), so a user sort is
  // monotonic across it. Empty title is the "headerless section" convention
  // consumed by buildDisplayRows.
  const sections = useMemo(() => buildSections(filteredTradesData), [filteredTradesData]);

  const visibleIds = useMemo(() => sections.flatMap((s) => s.items.map((t) => t.id)), [sections]);

  // Drop selection when the selected row is hidden by a sidebar filter or
  // search query change — otherwise the FloatingActionBar would vanish while
  // the id stayed in the Set and would resurface once the filter was reverted
  // (kvi-r1 #1).
  // Not during a first load: the window is empty then, and pruning to it would drop
  // rows that come back on the first page. Nor under placeholder data: the held rows
  // are the previous selection's (empty, after a disabled query), and the landing
  // prunes against the real ones.
  const { pruneToVisible } = selection;
  useEffect(() => {
    if (isBlotterLoading || isPlaceholderData) return;
    pruneToVisible(visibleIds);
  }, [visibleIds, pruneToVisible, isBlotterLoading, isPlaceholderData]);

  // No selected trade while a new selection loads, so the action bars hide as they did
  // under the old skeleton: the held rows are the previous selection's, its landing
  // prunes them (an open Bulk Re-Rate would lose its trades), and a mutation would not
  // restart the request already in flight. The ids themselves stay selected.
  const selectedTrade = useMemo(() => {
    if (isPlaceholderData || selection.selectedCount !== 1) return null;
    const selectedId = Array.from(selection.selectedIds)[0];
    return filteredTradesData.find((t) => t.id === selectedId) ?? null;
  }, [selection.selectedIds, selection.selectedCount, filteredTradesData, isPlaceholderData]);

  // LR-539: the full selected set (for the bulk action bar + staging modal).
  const selectedTrades = useMemo(
    () => (isPlaceholderData ? [] : filteredTradesData.filter((t) => selection.selectedIds.has(t.id))),
    [filteredTradesData, selection.selectedIds, isPlaceholderData],
  );
  const [bulkReRateOpen, setBulkReRateOpen] = useState(false);

  const { selectAllVisible, selectOnly, toggleRow, selectRange } = selection;

  // LR-699: a row click carries the mode its modifiers asked for. A counterparty
  // tab opens with nothing selected — the LR-539 default that pre-selected every
  // active trade of the CP is gone: it put the book in bulk mode before the
  // trader had clicked anything.
  // Nor selecting on the held rows (see `selectedTrade`).
  const handleRowClick = useCallback(
    (id: number, mode: RowSelectionMode) => {
      if (isPlaceholderData) return;
      applySelectionMode({ selectOnly, toggleRow, selectRange }, mode, id, visibleIds);
    },
    [selectOnly, toggleRow, selectRange, visibleIds, isPlaceholderData]
  );

  // Shift+ArrowUp/Down: the shortcut hook moves the focus ring, and the range
  // follows it from the same anchor — the chord every list uses to grow a
  // selection would otherwise only move focus. Arrow keys are the shortcut
  // hook's alone (unlike Space/Enter, which belong to the row), so extending
  // from there re-creates no double-handling.
  const handleExtendSelectionTo = useCallback(
    (id: number) => {
      if (!isPlaceholderData) selectRange(id, visibleIds);
    },
    [selectRange, visibleIds, isPlaceholderData]
  );

  // Copy current view link to clipboard (KEY-03)
  const handleCopyLink = useCallback(() => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).catch(() => {
      // Fallback: silently fail
    });
  }, []);

  // Get visible row IDs for keyboard navigation (Arrow Up/Down)
  const getVisibleRowIds = useCallback(() => visibleIds, [visibleIds]);

  useKeyboardShortcuts({
    onCopyLink: handleCopyLink,
    onOpenTradeEntry: () => setIsCreateTradeModalOpen(true),
    onExtendSelectionTo: handleExtendSelectionTo,
    getVisibleRowIds,
    hasSelection: selection.hasSelection,
    selectedCount: selection.selectedCount,
  });

  // Sync sorted data ref for context menus
  sortedDataRef.current = filteredTradesData;
  currentTradesRef.current = filteredTradesData;

  // Sync to store for MetricsBar (use-view-metrics reads from store).
  // 1-render lag is acceptable for display-only metrics.
  useEffect(() => {
    setFilteredTradesData(filteredTradesData);
  }, [filteredTradesData, setFilteredTradesData]);

  useEffect(() => {
    // Tag the publish with the view+mode that produced it (same render as the
    // memo above) — MetricsBar's stranded-preset auto-clear must not act on a
    // previous tab's book or on the history-mode set (LR-518 r2 #0). Placeholder
    // rows belong to the previous selection, so they are not published at all.
    if (isPlaceholderData) return;
    setViewScopedTradesData(
      viewScopedTradesData,
      `${activeViewId}|${isHistoryMode ? 'history' : 'live'}`,
    );
  }, [viewScopedTradesData, setViewScopedTradesData, activeViewId, isHistoryMode, isPlaceholderData]);

  // Publish the list scope + server total so the sidebar CSV export fetches the
  // WHOLE filtered book (the blotter is windowed) and labels the true row count.
  // The scope goes out at once: the export fetches its own rows and applies the live
  // sidebar filters to them, so it must be the new selection's. Its total is unknown
  // until the first page lands (placeholder data carries the previous selection's).
  useEffect(() => {
    setTradesListParams(serverParams);
  }, [serverParams, setTradesListParams]);
  useEffect(() => {
    setTradesTotalCount(isPlaceholderData ? undefined : totalCount);
  }, [totalCount, setTradesTotalCount, isPlaceholderData]);

  useEffect(() => {
    setIsTradesDataLoading(isBlotterLoading);
    setTradesDataError(isBlotterError);
  }, [isBlotterLoading, isBlotterError, setIsTradesDataLoading, setTradesDataError]);

  useEffect(() => {
    // History → Mark to Market / Cash Pool are self-contained log views; the trades filter
    // sidebar does not apply there, so hide it for those subtabs and keep it on everywhere else.
    setShowFilterSidebar(!showHistoryLog);
    return () => {
      setShowFilterSidebar(false);
    };
  }, [setShowFilterSidebar, showHistoryLog]);

  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // Server total for the search, not the loaded window (which grows on scroll).
  // Suppressed (undefined → the bar shows nothing) while loading/errored so it never
  // reads "0 found" mid-flight, and when a client-only predicate (dailyEarnings, which
  // the server can't filter) is active — else the server total would overcount the
  // rows actually shown (kvi-r2 #5).
  const hasClientOnlyFilter =
    localTradesFilters.dailyEarningsMin !== null || localTradesFilters.dailyEarningsMax !== null;
  const resultCount =
    searchQuery.trim() && !isBlotterLoading && !isPlaceholderData && !isBlotterError && !hasClientOnlyFilter
      ? totalCount
      : undefined;

  // LR-390/LR-479: History → Mark to Market / Cash Pool are self-contained log views — the trades
  // filters, search and Trade Entry do not apply, so render them instead of the trades-table shell.
  if (showHistoryLog) {
    return (
      <div className="relative flex h-[calc(100vh-0px)] flex-col">
        {showMtmHistory && <TourSentinel name="history-mtm-overview" />}
        <Card className="flex flex-1 flex-col overflow-hidden rounded-none border-none">
          <CardHeader className="flex flex-col gap-2 py-2">
            <TradesModeTabs activeMode="history" />
            <HistorySubTabs activeHTab={showCashPoolHistory ? 'cashpool' : 'mtm'} />
          </CardHeader>
          <CardContent className="flex flex-1 flex-col overflow-hidden p-0 relative">
            {/* key by tab so the local search query resets when switching MTM ↔ Cash Pool
                (hview/sel are URL-driven and reset by HistorySubTabs; the query is not). */}
            <HistoryShell
              key={showCashPoolHistory ? 'cashpool' : 'mtm'}
              tab={showCashPoolHistory ? 'cashpool' : 'mtm'}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative flex h-[calc(100vh-0px)] flex-col">
      {/* Same shell serves the live blotter and the History → Trades archive;
          give each its own sentinel so the onboarding tour can spotlight the
          right table (live "Your trades" vs. the terminal-trades archive). */}
      <TourSentinel name={isHistoryMode ? 'history-trades-overview' : 'trades-overview'} />
      <Card className="flex flex-1 flex-col overflow-hidden rounded-none border-none">
        <CardHeader className="flex flex-col gap-2 py-2">
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <TradesModeTabs activeMode={isHistoryMode ? 'history' : 'trades'} />
            </div>
            <div className="flex items-center gap-3">
              {stripChips.length > 0 && (
                <button
                  type="button"
                  onClick={() => setAppliedFiltersExpanded(!appliedFiltersExpanded)}
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 focus:outline-none"
                  aria-expanded={appliedFiltersExpanded}
                  aria-label="Applied filters"
                >
                  Applied filters: {stripChips.length}
                  {appliedFiltersExpanded ? (
                    <ChevronUpIcon className="h-4 w-4 text-blue-600" aria-hidden />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 text-blue-600" aria-hidden />
                  )}
                </button>
              )}
              <div className="w-80">
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search id, symbol, ISIN, counterparty, status…"
                  resultCount={resultCount}
                />
              </div>
              {canTrade && (
                <button
                  data-tour="trade-entry-button"
                  onClick={() => setIsCreateTradeModalOpen(true)}
                  className="flex items-center justify-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-colors whitespace-nowrap"
                >
                  <PencilSquareIcon className="h-4 w-4 stroke-2" />
                  Trade Entry
                </button>
              )}
              <HelpLink to="trades" iconOnly label="Trades docs" />
            </div>
          </div>
          {isHistoryMode && <HistorySubTabs activeHTab="trades" />}
        </CardHeader>
        {/* LR-672: the counterparty tabs are the section's scope, shown on the
            live blotter AND on History → Trades (All Trades stays the global
            terminal log LR-263 asked for; a CP tab now makes its scope explicit
            instead of silently applying the last live pick). SLB/Repo metrics
            stay live-only — History rows are never open-book. */}
        {showViewTabBar && (
          <ViewTabBar
            tradeCounts={viewTradeCounts}
            // Until the first summary arrives; later refetches keep the previous counts.
            countsLoading={tabCountSummary === undefined && !tabCountsFailed}
            countsFailed={tabCountSummary === undefined && tabCountsFailed}
            // LR-700: in History the Term pill rides the tab row — there is no
            // MetricsBar to host it there, and a filter is not a metric, so this
            // does not resurrect the bar History deliberately lacks. In live mode
            // the pill lives in MetricsBar next to Settlement Ccy, so the slot
            // stays empty and the pill is never rendered twice.
            trailing={
              isHistoryMode ? (
                <TermFilterPill
                  totals={termPillSummary?.term_type_totals}
                  selected={localTradesFilters.termTypes}
                  onChange={handleTermTypesChange}
                />
              ) : undefined
            }
          />
        )}
        {showMetricsBar && (
          <MetricsBar agreementType={activeViewAgreementType} cpNameToId={cpNameToId} cpScopeReady={cpScopeReady} />
        )}
        {/* The strip no longer lists the settlement-ccy chip (its home is the
            summary-bar pill). Clear all still wipes ALL local filters incl. the
            ccy — "clear all" means all — which also resets the pill's outline. */}
        <AppliedFiltersStrip
          count={stripChips.length}
          chips={stripChips}
          onChipClick={handleAppliedChipClick}
          onChipRemove={handleAppliedChipRemove}
          onClearAll={clearLocalTradesFilters}
          hideTrigger
          expanded={appliedFiltersExpanded}
          onToggleExpanded={setAppliedFiltersExpanded}
        />
        <CardContent className="flex flex-1 flex-col overflow-hidden p-0 border-t border-gray-200 relative">
          <TradesTable
            sections={sections}
            columns={columns}
            sorting={sorting}
            onSortChange={setSorting}
            columnWidths={columnWidths}
            isLoading={isBlotterLoading}
            isRefreshing={isPlaceholderData}
            error={blotterError}
            onLoadMore={fetchNextPage}
            hasNextPage={hasNextPage && !isPlaceholderData}
            isFetchingNextPage={isFetchingNextPage}
            loaderResetKey={serverParamsKey}
            footerColumnTotals={heldFooterColumnTotals}
            emptyState={tableEmptyState}
            hoveredColumn={hoveredColumn}
            onHoveredColumnChange={setHoveredColumn}
            onShowScrollTop={setShowScrollTop}
            scrollContainerRef={scrollContainerRef}
            selectedIds={selection.selectedIds}
            // The bars are hidden under placeholder data (see `selectedTrade`), so nothing to clear.
            hasSelection={selection.hasSelection && !isPlaceholderData}
            fabClearancePx={fabClearanceFor(actionBarHeight)}
            onRowClick={handleRowClick}
            pinnedLeft={pinnedLeft}
            pinnedRight={pinnedRight}
            onPinColumn={pinColumn}
            activeFilterColumnIds={activeFilterColumnIds}
            onFilterColumnClick={handleFilterColumnClick}
            filterableColumnIds={TRADES_FILTERABLE_COLUMN_IDS}
            onContextMenuColumn={handleColumnContextMenu}
            isColumnsTabActive={isColumnsTabActive}
            highlightedColumnIds={highlightedColumnIds}
            searchQuery={heldSearchQuery}
          />
        </CardContent>
        <ScrollToTopButton show={showScrollTop} onClick={scrollToTop} />

        <FloatingActionBar
          selectedTrade={selectedTrade}
          selectedCount={selection.selectedCount}
          onClearSelection={selection.clearSelection}
          onOpen={handleOpen}
          onAction={handlePanelAction}
          onMtm={handleMarkToMarket}
          onShowBoError={handleShowBoError}
          canTrade={canTrade}
          onHeightChange={setActionBarHeight}
          scrollTopVisible={showScrollTop}
        />

        {/* LR-539: multi-select bar (>1 rows) — the single-row FAB above renders at exactly 1. */}
        <BulkActionBar
          selectedTrades={selectedTrades}
          visibleCount={visibleIds.length}
          allVisibleSelected={selection.isAllVisibleSelected(visibleIds)}
          onSelectAllVisible={() => {
            if (!isPlaceholderData) selectAllVisible(visibleIds);
          }}
          onReRate={() => setBulkReRateOpen(true)}
          onClear={selection.clearSelection}
          canTrade={canTrade}
          onHeightChange={setActionBarHeight}
          scrollTopVisible={showScrollTop}
        />

        <BulkReRateModal
          isOpen={bulkReRateOpen}
          onClose={() => setBulkReRateOpen(false)}
          onApplied={selection.clearSelection}
          trades={selectedTrades}
        />

        {/* LR-539: single re-rate — mounted once the on-demand PositionDetail has
            loaded (ReRateModal requires a defined trade). */}
        {reRateTradeId !== null && reRateDetail && (
          <ReRateModal
            isOpen
            onClose={() => setReRateTradeId(null)}
            trade={reRateDetail}
            reRate={reRate}
          />
        )}

        <PartialRecallModal
          isOpen={!!partialRecallTrade}
          trade={partialRecallTrade}
          onClose={() => setPartialRecallTrade(null)}
          partialRecall={partialRecall}
        />

        <AmendTradeModalConnector
          isOpen={!!amendTrade}
          tradeId={amendTrade?.id ?? 0}
          onClose={() => setAmendTrade(null)}
          updatePosition={updatePosition}
        />

        {headerCtxMenu && (
          <ContextMenu
            items={headerCtxMenuItems}
            position={{ x: headerCtxMenu.x, y: headerCtxMenu.y }}
            onClose={closeHeaderCtxMenu}
          />
        )}

        <ConfirmDialog
          show={!!noticeTrade}
          onClose={() => setNoticeTrade(null)}
          title="Give Notice"
          confirmLabel="Confirm"
          isLoading={isGivingNotice}
          onConfirm={confirmGiveNotice}
          error={noticeError}
          errorFallback="Failed to give notice. Please try again."
        >
          <p className="text-sm text-gray-500">
            Are you sure you want to give notice for position <strong>{noticeTrade?.id}</strong> ({noticeTrade?.symbol_id})?
            This action is irreversible and will fix the maturity date based on the notice period.
          </p>
          <div className="mt-4">
            <label htmlFor="notice_date" className="block text-xs text-gray-500 mb-1">
              Notice Date
            </label>
            <input
              type="date"
              id="notice_date"
              value={noticeDate}
              // LR-696 round 4: the book is the ONLY place a notice can be served
              // with a past date. The card's NoticeModal has no date field at all
              // — it always submits today — and `Position.give_notice` validates
              // nothing, writing `notice_served_date` verbatim and shortening the
              // term by however many days back you go. That is a contractual
              // record, so the field is narrowed to what the card already allows.
              min={todayLocalKey()}
              onChange={(e) => setNoticeDate(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </ConfirmDialog>

        <SetCloseDateDialog
          trade={settlementDateTrade}
          onClose={() => setSettlementDateTrade(null)}
          date={actualSettlementDate}
          onDateChange={setActualSettlementDate}
          reason={settlementReason}
          onReasonChange={setSettlementReason}
          holidays={holidays}
          isLoading={isSettingSettlementDate}
          error={settlementDateError}
          onConfirm={confirmSetSettlementDate}
        />

        <ConfirmDialog
          show={!!terminateTradeItem}
          onClose={() => setTerminateTradeItem(null)}
          title="Terminate Position"
          titleColor="red"
          confirmLabel="Terminate"
          confirmColor="red"
          isLoading={isTerminating}
          onConfirm={confirmTerminate}
          error={terminateError}
          errorFallback="Failed to terminate position. Please try again."
        >
          <p className="text-sm text-gray-500">
            Are you sure you want to terminate position <strong>{terminateTradeItem?.id}</strong> ({terminateTradeItem?.symbol_id})?
            {/* LR-695: Terminate fixes the day the loan ends — maturity and close
                date together; the status follows on that day, not on Confirm. */}
            {' '}This fixes the date the loan ends: the position stays active and accrues until then,
            then matures and closes on that date. Choosing today ends it now.
          </p>
          <div className="mt-4">
            {/* LR-576 (feedback п.70): aligned with the STV TerminateModal wording. */}
            <label htmlFor="terminate_settlement_date" className="block text-xs text-gray-500 mb-1">
              Termination Date
            </label>
            <input
              type="date"
              id="terminate_settlement_date"
              value={terminateSettlementDate}
              // LR-696 round 4: mirrors the card's TerminateModal, which has
              // carried `min={todayLocalKey()}` all along. Without it a viewer
              // east of UTC could submit yesterday and spend a 400 to find out —
              // an error the same action on the trade page makes unreachable.
              min={todayLocalKey()}
              onChange={(e) => setTerminateSettlementDate(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="mt-4">
            <label htmlFor="terminate_reason" className="block text-xs text-gray-500 mb-1">
              Reason (optional)
            </label>
            <textarea
              id="terminate_reason"
              value={terminateReason}
              onChange={(e) => setTerminateReason(e.target.value)}
              placeholder="e.g. Recall - client needs securities back"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
              rows={2}
            />
          </div>
        </ConfirmDialog>

        {/* LR-408: cancel-flow for rejected positions. No settlement date —
            position never opened in BO. */}
        <ConfirmDialog
          show={!!cancelRejectedItem}
          onClose={() => setCancelRejectedItem(null)}
          title="Cancel Rejected Position"
          titleColor="red"
          confirmLabel="Cancel Position"
          confirmColor="red"
          isLoading={isTerminating}
          onConfirm={confirmCancelRejected}
          error={terminateError}
          errorFallback="Failed to cancel position. Please try again."
        >
          <p className="text-sm text-gray-500">
            Cancel position <strong>{cancelRejectedItem?.id}</strong> ({cancelRejectedItem?.symbol_id})?
            BO rejected this position {cancelRejectedItem?.bo_retry_count ?? 0} time(s) — nothing was opened, no settlement is required.
          </p>
          <BoReasonNote trade={cancelRejectedItem} />
          <div className="mt-4">
            <label htmlFor="cancel_rejected_reason" className="block text-xs text-gray-500 mb-1">
              Reason (optional)
            </label>
            <textarea
              id="cancel_rejected_reason"
              value={cancelRejectedReason}
              onChange={(e) => setCancelRejectedReason(e.target.value)}
              placeholder="e.g. BO account permanently invalid"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
              rows={2}
            />
          </div>
        </ConfirmDialog>

        {/* LR-408: confirm retry — soft guard so operator acknowledges they
            fixed the underlying BO data before flipping rejected→pending. */}
        <ConfirmDialog
          show={!!retryTradeItem}
          onClose={() => setRetryTradeItem(null)}
          title="Retry Rejected Position"
          confirmLabel="Retry"
          confirmColor="blue"
          isLoading={isRetrying}
          onConfirm={confirmRetry}
          error={retryError}
          errorFallback="Failed to reset position. Please try again."
        >
          <p className="text-sm text-gray-500">
            Reset position <strong>{retryTradeItem?.id}</strong> ({retryTradeItem?.symbol_id}) back to <code>pending</code>?
            The hourly worker will retry the BO call on its next run. Make sure the underlying BO data
            (counterparty account, symbol mapping, etc.) has been fixed first.
          </p>
          <BoReasonNote trade={retryTradeItem} />
        </ConfirmDialog>

        {/* LR-408: BO error details — read-only inspector opened from the
            Rejected badge / retry counter chip. Copy button + monospace
            display so operators can paste the full BO response into Slack
            or a ticket. */}
        <ConfirmDialog
          show={!!boErrorTrade}
          onClose={() => setBoErrorTrade(null)}
          title="BO error details"
          confirmLabel="Close"
          confirmColor="blue"
          isLoading={false}
          onConfirm={() => setBoErrorTrade(null)}
        >
          <p className="text-sm text-gray-500">
            Position <strong>{boErrorTrade?.id}</strong> ({boErrorTrade?.symbol_id}) — what
            BackOffice did with this trade&rsquo;s last posting
            {(boErrorTrade?.bo_retry_count ?? 0) > 0
              ? `, after ${boErrorTrade?.bo_retry_count} attempt(s)`
              : ''}:
          </p>

          {/* LR-712: the state first, in words. It is the fact that decides what
              the operator may safely do next; the response body below is evidence
              for it, not a substitute — a body alone cannot say whether BackOffice
              may already hold the booking. */}
          {boErrorPosting && (
            <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2" data-testid="bo-error-state">
              <p className="text-sm font-medium text-red-800">
                {boPostingCopy(boErrorPosting).title}
              </p>
              <p className="mt-0.5 text-sm text-red-700">
                {boPostingCopy(boErrorPosting).detail}
              </p>
            </div>
          )}

          {boErrorLegacy && (
            <p className="mt-3 text-[11px] italic text-gray-500" data-testid="bo-error-legacy-caption">
              {LEGACY_BO_ERROR_CAPTION}
            </p>
          )}

          {/* The body arrives with the detail, a beat after the state does. Saying
              "BackOffice sent no response body" in that gap would be a false
              statement with a short life — the exact defect this ticket is about,
              so the placeholder waits for an answer before making a claim. */}
          <div className="mt-3 flex items-start gap-2">
            <pre className="flex-1 p-2 bg-red-50 border border-red-200 rounded text-[11px] font-mono text-red-800 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
              {boErrorBody || (boErrorLoading
                ? 'Loading BackOffice’s response…'
                : '(BackOffice sent no response body)')}
            </pre>
            <button
              type="button"
              onClick={copyBoError}
              disabled={!boErrorBody}
              className="shrink-0 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {boErrorCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </ConfirmDialog>

        <ConfirmDialog
          show={!!settleTradeItem}
          onClose={() => setSettleTradeItem(null)}
          title="Settle Position"
          confirmLabel="Settle"
          confirmColor="green"
          isLoading={isSettling}
          onConfirm={confirmSettle}
          error={settleError}
          errorFallback="Failed to settle position. Please try again."
          // LR-581: the BO reason (400 refusal / 502 outage) arrives in `detail` —
          // show it verbatim instead of the capped generic line.
          showResponseDetail
        >
          <p className="text-sm text-gray-500">
            Are you sure you want to settle position <strong>{settleTradeItem?.id}</strong> ({settleTradeItem?.symbol_id})?
            This will create backoffice transactions and activate the position.
          </p>
        </ConfirmDialog>

        <ConfirmDialog
          show={!!settleCashItem}
          onClose={() => setSettleCashItem(null)}
          title={`Settle ${collateralLegName(settleCashItem?.collateral_type)} Leg`}
          confirmLabel={`Settle ${collateralLegName(settleCashItem?.collateral_type)}`}
          confirmColor="green"
          isLoading={isSettlingCash}
          onConfirm={confirmSettleCash}
          error={settleCashError}
          errorFallback={`Failed to settle ${collateralLegName(settleCashItem?.collateral_type).toLowerCase()} leg.`}
          showResponseDetail
        >
          <p className="text-sm text-gray-500">
            Settle {collateralLegName(settleCashItem?.collateral_type).toLowerCase()} leg of position <strong>{settleCashItem?.id}</strong> ({settleCashItem?.symbol_id}).
            Securities leg will remain pending until settled separately.
          </p>
        </ConfirmDialog>

        <ConfirmDialog
          show={!!settleSecuritiesItem}
          onClose={() => setSettleSecuritiesItem(null)}
          title="Settle Securities Leg"
          confirmLabel="Settle Securities"
          confirmColor="green"
          isLoading={isSettlingSecurities}
          onConfirm={confirmSettleSecurities}
          error={settleSecuritiesError}
          errorFallback="Failed to settle securities leg."
          showResponseDetail
        >
          <p className="text-sm text-gray-500">
            Settle securities leg of position <strong>{settleSecuritiesItem?.id}</strong> ({settleSecuritiesItem?.symbol_id}).
            {collateralLegName(settleSecuritiesItem?.collateral_type)} leg will remain pending until settled separately.
          </p>
        </ConfirmDialog>

      </Card>
    </div>
  );
};
